/**
 * tokenRefresh.js
 * Utilities for silent JWT access token refresh on the client.
 *
 * Strategy:
 *   - Access token: 4h (short-lived, stored in localStorage as 'token')
 *   - Refresh token: 7d (long-lived, stored in localStorage as 'refreshToken')
 *   - Silent refresh fires 10 minutes before access token expiry
 *   - On refresh failure the caller should log the user out
 */

const REFRESH_BUFFER_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Decode a JWT payload without verifying the signature.
 * Safe for client-side use — we only need the `exp` claim.
 * Returns the decoded payload or null on failure.
 */
export function decodeTokenPayload(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Returns milliseconds until the token expires, or 0 if already expired / unreadable.
 */
export function msUntilExpiry(token) {
  const payload = decodeTokenPayload(token);
  if (!payload || !payload.exp) return 0;
  const expiryMs = payload.exp * 1000;
  return Math.max(0, expiryMs - Date.now());
}

/**
 * Call the server's token refresh endpoint.
 * On success: stores new access token in localStorage and returns it.
 * On failure: returns null.
 */
export async function refreshAccessToken(apiUrl) {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${apiUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data.success && data.token) {
      localStorage.setItem('token', data.token);
      return data.token;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Schedule a silent token refresh REFRESH_BUFFER_MS before the access token expires.
 *
 * @param {string}   token        - Current access token
 * @param {string}   apiUrl       - API base URL
 * @param {function} onRefresh    - Called with the new token string on success
 * @param {function} onExpired    - Called (no args) when refresh token is also expired
 * @returns {function}            - Cancel function — call on logout/unmount
 */
export function scheduleTokenRefresh(token, apiUrl, onRefresh, onExpired) {
  const remaining = msUntilExpiry(token);
  const delay = Math.max(0, remaining - REFRESH_BUFFER_MS);

  let timeoutId = null;

  const doRefresh = async () => {
    const newToken = await refreshAccessToken(apiUrl);
    if (newToken) {
      onRefresh(newToken);
      // Reschedule for the new token
      timeoutId = setTimeout(doRefresh, Math.max(0, msUntilExpiry(newToken) - REFRESH_BUFFER_MS));
    } else {
      // Refresh token expired or invalid — force logout
      onExpired();
    }
  };

  timeoutId = setTimeout(doRefresh, delay);

  // Return cancel function
  return () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
}
